import { TimeDrivenCache } from "./time-driven-cache";

type FetchParameters = Parameters<typeof window.fetch>;
type FetchReturnType = ReturnType<typeof window.fetch>;

class FetchError extends Error {
  response: Response;
  status: Response["status"];

  constructor({ response }: { response: Response }) {
    super(`Fetch Error (Status: ${response.status}, URL: "${response.url}")`);
    this.response = response;
    this.status = response.status;
  }
}

export class FetchAugmented {
  private readonly cache: TimeDrivenCache<FetchParameters[0], FetchReturnType>;
  private readonly origin: string;

  constructor({ cacheLifetime, origin }: { cacheLifetime: number; origin: string }) {
    this.isOriginValid({ origin });

    this.cache = new TimeDrivenCache({ lifetime: cacheLifetime });
    this.origin = origin;
  }

  private isOriginValid({ origin }: { origin: string }) {
    try {
      new URL(origin);
    } catch (e) {
      throw new Error("FetchAugmented Error: Origin should be valid");
    }

    if (new URL(origin).origin !== origin) {
      throw new Error("FetchAugmented Error: Origin should be an origin, not whole URL");
    }

    return origin;
  }

  private augmentRequestInfo({ requestInfo }: { requestInfo: FetchParameters[0] }): FetchParameters[0] {
    if (typeof requestInfo === "string") {
      return new URL(requestInfo, this.origin);
    }

    return requestInfo;
  }

  private async fetch(...args: FetchParameters) {
    const [requestInfo, requestInit] = args;

    const response = await window.fetch(this.augmentRequestInfo({ requestInfo }), requestInit);

    if (!response.ok) {
      throw new FetchError({ response });
    }

    return response;
  }

  async get(...args: FetchParameters) {
    const [requestInfo, requestInit = {}] = args;

    if (this.cache.hasItem({ key: requestInfo })) {
      return this.cache.getItem({ key: requestInfo })!;
    }

    return this.cache.setItem({
      key: requestInfo,
      value: this.fetch(requestInfo, { ...requestInit, method: "GET" }),
    });
  }

  async post(...args: FetchParameters) {
    const [requestInfo, requestInit = {}] = args;
    return this.fetch(requestInfo, { ...requestInit, method: "POST" });
  }

  async delete(...args: FetchParameters) {
    const [requestInfo, requestInit = {}] = args;
    return this.fetch(requestInfo, { ...requestInit, method: "DELETE" });
  }

  async put(...args: FetchParameters) {
    const [requestInfo, requestInit = {}] = args;
    return this.fetch(requestInfo, { ...requestInit, method: "PUT" });
  }

  async patch(...args: FetchParameters) {
    const [requestInfo, requestInit = {}] = args;
    return this.fetch(requestInfo, { ...requestInit, method: "PATCH" });
  }
}
